import React, { useEffect, useState } from 'react';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { CheckCircle2, ExternalLink } from 'lucide-react';
import DiffView from '../../components/DiffView';
import { apiErrorMessage, marketingSiteService } from '../../services/apiService';
import { useAuthToken } from '../../hooks/useAuthToken';
import { useDeployStatus } from './useDeployStatus';
import { isDeployInProgress } from './useReleaseStatus';
import { DeployBadge, htmlToText, shortSha, truncate } from './ui';
import SeoFindings from './SeoFindings';

/** What the edit does to the page's SEO findings; notes are left out unless they are all there is. */
const SeoDelta = ({ lint }) => {
  if (!lint) return null;
  const important = (list) => list.filter((f) => f.severity !== 'INFO');
  const fixed = lint.fixed || [];
  const introduced = lint.introduced || [];
  if (fixed.length === 0 && introduced.length === 0) return null;
  const summary = [
    fixed.length > 0 && `fixes ${fixed.length}`,
    introduced.length > 0 && `introduces ${introduced.length}`,
  ]
    .filter(Boolean)
    .join(', ');
  const shownIntroduced = important(introduced).length > 0 ? important(introduced) : introduced;
  return (
    <div className="mse-seo-delta mt-3">
      <div className="mse-sublabel">SEO: this change {summary}</div>
      {shownIntroduced.length > 0 && <SeoFindings findings={shownIntroduced} />}
      {fixed.length > 0 && (
        <div className="mse-hint">Fixed: {fixed.map((f) => f.title.toLowerCase()).join(', ')}</div>
      )}
    </div>
  );
};

/** The API only takes the unit id, new value and the range the client saw. */
const toPayload = (editList) => editList.map(({ id, value, start, end }) => ({ id, value, start, end }));

/**
 * Review and save: shows the pending edits and the server's diff, takes a commit message, then
 * commits to main and follows the resulting test-site deploy.
 */
const SaveModal = ({ show, page, editList, onClose, onSaved, onReloadPage }) => {
  const ensureToken = useAuthToken();
  const [diff, setDiff] = useState(null);
  const [diffError, setDiffError] = useState(null);
  const [conflict, setConflict] = useState(false);
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [result, setResult] = useState(null);
  const [showDiff, setShowDiff] = useState(false);
  const deploy = useDeployStatus(result?.deploy);

  useEffect(() => {
    if (!show) return;
    setDiff(null);
    setDiffError(null);
    setConflict(false);
    setSaveError(null);
    setResult(null);
    setShowDiff(false);
    setMessage('');
    let cancelled = false;
    (async () => {
      try {
        await ensureToken();
        const response = await marketingSiteService.diff({ path: page.path, sha: page.sha, edits: toPayload(editList) });
        if (!cancelled) setDiff(response.data);
      } catch (err) {
        if (cancelled) return;
        setConflict(err?.response?.status === 409);
        setDiffError(apiErrorMessage(err, 'Could not prepare the changes.'));
      }
    })();
    return () => {
      cancelled = true;
    };
    // Only when the dialog opens: the edits cannot change while it is open.
  }, [show]);

  const save = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      await ensureToken();
      const response = await marketingSiteService.save({
        path: page.path,
        sha: page.sha,
        edits: toPayload(editList),
        message: message.trim() || undefined,
      });
      setResult(response.data);
      onSaved(response.data);
    } catch (err) {
      setConflict(err?.response?.status === 409);
      setSaveError(apiErrorMessage(err, 'Saving failed.'));
    } finally {
      setSaving(false);
    }
  };

  const changedIds = new Set(diff?.changedIds || []);
  const changes = editList.filter((e) => changedIds.has(e.id));
  const longLines = (diff?.patch || '').split('\n').some((l) => l.length > 400);
  // The test site shows the old page until its deploy finishes
  const deployRunning = isDeployInProgress(deploy);

  return (
    <Modal show={show} onHide={saving ? undefined : onClose} size="lg" fullscreen="sm-down" backdrop={saving ? 'static' : true}>
      <Modal.Header closeButton={!saving}>
        <Modal.Title>{result ? 'Saved' : `Save changes to ${page?.url}`}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {result ? (
          <div>
            <p className="d-flex align-items-center gap-2">
              <CheckCircle2 size={18} className="text-success" />
              Committed{' '}
              <a href={result.commitUrl} target="_blank" rel="noopener noreferrer">
                <code>{shortSha(result.commitSha)}</code>
              </a>
              to the test site.
            </p>
            <div className="d-flex align-items-center gap-2 mb-1">
              Test site deploy: <DeployBadge deploy={deploy} />
              {deployRunning && <Spinner animation="border" size="sm" />}
            </div>
            {deploy?.detail && <p className="mse-hint">{deploy.detail}</p>}
            <p className="mse-hint mb-0">
              The change reaches the test site first. Use Deploy to production in the bar above when you are ready.
            </p>
          </div>
        ) : (
          <>
            {diffError && (
              <Alert variant={conflict ? 'warning' : 'danger'}>
                {diffError}
                {conflict && (
                  <div className="mt-2">
                    <Button size="sm" variant="outline-secondary" onClick={onReloadPage}>
                      Reload page (discards your edits)
                    </Button>
                  </div>
                )}
              </Alert>
            )}
            {!diff && !diffError && (
              <div className="d-flex align-items-center gap-2 text-muted">
                <Spinner animation="border" size="sm" /> Preparing changes
              </div>
            )}
            {diff && (
              <>
                {changes.length === 0 ? (
                  <Alert variant="secondary">None of the edits change the page source.</Alert>
                ) : (
                  <div className="mse-save-list">
                    {changes.map((e) => (
                      <div key={e.id} className="mse-change">
                        <div className="mse-sublabel">{e.label}</div>
                        <div className="mse-before">{truncate(e.kind === 'HTML' ? htmlToText(e.original) : e.original, 300) || '(empty)'}</div>
                        <div className="mse-after">{truncate(e.kind === 'HTML' ? htmlToText(e.value) : e.value, 300) || '(empty)'}</div>
                      </div>
                    ))}
                  </div>
                )}
                <SeoDelta lint={diff.lint} />
                {diff.links?.introduced?.length > 0 && (
                  <Alert variant="warning" className="mt-3 mb-0">
                    <div className="mse-sublabel mb-1">This change adds links that do not work:</div>
                    <SeoFindings findings={diff.links.introduced} />
                  </Alert>
                )}
                <button type="button" className="mse-link-button mt-2" onClick={() => setShowDiff((v) => !v)}>
                  {showDiff ? 'Hide' : 'Show'} source diff{longLines ? ' (long lines wrap)' : ''}
                </button>
                {showDiff && <DiffView patch={diff.patch} />}
                <Form.Group className="mt-3">
                  <Form.Label>Commit message</Form.Label>
                  <Form.Control
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder={`Edit ${page?.url} via admin editor`}
                    disabled={saving}
                  />
                </Form.Group>
              </>
            )}
            {saveError && (
              <Alert variant={conflict ? 'warning' : 'danger'} className="mt-3 mb-0">
                {saveError}
                {conflict && (
                  <div className="mt-2">
                    <Button size="sm" variant="outline-secondary" onClick={onReloadPage}>
                      Reload page (discards your edits)
                    </Button>
                  </div>
                )}
              </Alert>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        {result ? (
          <>
            <Button
              variant="outline-secondary"
              href={page?.testUrl}
              target="_blank"
              rel="noopener noreferrer"
              disabled={deployRunning}
              title={deployRunning ? 'Available once the test site deploy finishes' : undefined}
            >
              <ExternalLink size={14} className="me-1" />
              {deployRunning ? 'Deploying to test site' : 'Open on test site'}
            </Button>
            <Button variant="primary" onClick={onClose}>
              Done
            </Button>
          </>
        ) : (
          <>
            <Button variant="outline-secondary" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button variant="primary" onClick={save} disabled={saving || !diff || changes.length === 0}>
              {saving ? 'Saving' : 'Save to test site'}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default SaveModal;
